import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from three different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Bundle tier selection (`titan:price:update` from bundle-selector.js)
 *
 * ⚠️ (3) exists because the header price block was DEAD: it subscribed to variant
 * updates only, so picking "BUY 2 GET 20% OFF" left it showing the single-unit
 * price while the offer card below showed the real bundle total. The bundle tier is
 * a different axis from the variant and publishes its own event.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** Last tier payload seen, so it can be re-applied after a variant morph. */
  #lastTierDetail = null;

  connectedCallback() {
    super.connectedCallback();

    // ⚠️ Registered BEFORE the closestSection guard below. `titan:price:update` is
    // dispatched on `document`, not on the section, so it must bind even for
    // instances that have no .shopify-section ancestor (product cards, dialogs) —
    // otherwise this silently never binds.
    document.addEventListener('titan:price:update', this.updateTierPrice);

    // Pull a fresh tier broadcast rather than waiting for one. Without this a
    // <product-price> that is created or morphed AFTER the bundle's last broadcast
    // (the sticky bar is morphed wholesale on every variant change) comes up showing
    // server-rendered single-variant HTML and never repaints. Same race, and the same
    // fix, that offer-group.js:80-84 already uses.
    queueMicrotask(() => this.#requestTierRepublish());

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /** Ask the bundle group in this section to re-publish its current tier price. */
  #requestTierRepublish() {
    const section = this.closest('.shopify-section');
    if (!section) return;
    const bundle = section.querySelector('bundle-group-component');
    if (bundle && typeof bundle.publishSubtotal === 'function') bundle.publishSubtotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('titan:price:update', this.updateTierPrice);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Does this instance belong to the section that published the event?
   * detail.sectionId is the RAW `section.id`; at runtime the ancestor element is
   * `.shopify-section` with id `shopify-section-<section.id>`. Two product sections
   * on one page would otherwise fight over the same price block.
   * @param {string|null|undefined} sectionId
   */
  #matchesSection(sectionId) {
    if (!sectionId) return true;
    const section = this.closest('.shopify-section');
    if (!section) return false;
    return section.id === `shopify-section-${sectionId}`;
  }

  /** The live price container — re-queried because updatePrice replaces the node. */
  #container() {
    return this.querySelector('[ref="priceContainer"]');
  }

  /**
   * Swap only the NUMBER inside an existing label, preserving its wording.
   * ⚠️ The savings label is a Theme Setting (`settings.discount_badge_text`, with a
   * `[percent]` placeholder). Rebuilding the string here would hard-code English and
   * break the moment the wording or language changes — so replace the digits only.
   * @param {string} text
   * @param {number} percent
   */
  #swapPercent(text, percent) {
    if (typeof text !== 'string' || !text) return text;
    return text.replace(/\d+(?:[.,]\d+)?/, String(percent));
  }

  /**
   * Repaint the price block from a bundle tier payload.
   * @param {CustomEvent} event
   */
  updateTierPrice = (event) => {
    const detail = event && event.detail;
    if (!detail) return;
    if (!this.#matchesSection(detail.sectionId)) return;
    if (typeof detail.formattedTotal !== 'string') return;

    this.#lastTierDetail = detail;
    this.#paintTier(detail);
  };

  /** @param {any} detail */
  #paintTier(detail) {
    const container = this.#container();
    if (!container) return;

    const onSale = Boolean(detail.onSale);

    // Both arms are always in the DOM; one carries .price__hidden. A tier can flip
    // which arm is correct, so toggle the class rather than trusting the
    // server-rendered state.
    const regularArm = container.querySelector('.price__regular');
    const saleArm = container.querySelector('.price__sale');
    if (regularArm && saleArm) {
      regularArm.classList.toggle('price__hidden', onSale);
      saleArm.classList.toggle('price__hidden', !onSale);
    }

    // Current price — every node that shows it, in whichever arm is visible.
    for (const el of container.querySelectorAll('.price__regular .price, .price-item--sale')) {
      el.textContent = detail.formattedTotal;
    }

    // Compare-at — struck through. Hide it when there is nothing to compare to.
    for (const el of container.querySelectorAll('.compare-at-price')) {
      if (onSale && typeof detail.formattedCompareAt === 'string') {
        el.textContent = detail.formattedCompareAt;
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    }

    // Savings pill. Only ever present when the server rendered one (it requires
    // compare_at > price), so a product with no compare-at simply has nothing to
    // repaint — that is correct, not a missed case.
    const percent = Number(detail.savingsPercent);
    for (const badge of container.querySelectorAll('.titan-discount-badge')) {
      if (onSale && Number.isFinite(percent) && percent > 0) {
        badge.textContent = this.#swapPercent(badge.textContent || '', percent);
        const aria = badge.getAttribute('aria-label');
        if (aria) badge.setAttribute('aria-label', this.#swapPercent(aria, percent));
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) {
      // No matching node in the response — the sticky bar's <product-price> has no
      // data-block-id, so it lands here every time. Re-apply the tier price rather
      // than leaving the freshly-morphed server HTML (single-variant) on screen.
      if (this.#lastTierDetail) this.#paintTier(this.#lastTierDetail);
      this.#requestTierRepublish();
      return;
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // ⚠️ The swap above replaces priceContainer with SERVER HTML for the single
    // variant, which would silently undo the tier price. bundle-selector.js does
    // re-broadcast on variant change, but the ordering of the two is not guaranteed,
    // so re-apply the last known tier payload here rather than racing it.
    if (this.#lastTierDetail) this.#paintTier(this.#lastTierDetail);
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
